import { DestroyRef, inject, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import Keycloak from 'keycloak-js';

import { UserService } from './services/user';
import { ReferenceData } from './services/reference-data';
import { User } from './services/model/user';
import { ProfileActions } from './redux/actions/profile.actions';
import { ReferenceDataActions } from './redux/actions/reference-data.actions';
import { EstablishmentActions } from './redux/actions/establishment.actions';
import { selectAllProfiles } from './redux/selectors/profile.selectors';
import { selectAllEstablishments } from './redux/selectors/reference-data.selectors';

@Injectable({
  providedIn: 'root',
})
export class AppFacadeService {
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);
  private readonly keycloak = inject(Keycloak);
  private readonly router = inject(Router);
  private readonly referenceData = inject(ReferenceData);
  private readonly destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);

  constructor() {
    this.initUserAndEstablishments();
  }

  private initUserAndEstablishments(): void {
    const email: string | undefined = this.keycloak.idTokenParsed?.['email'];

    if (!email) {
      console.warn('[AppFacade] Aucun email trouvé dans le token Keycloak.');
      return;
    }

    this.userService.getUser(email).pipe(
      tap(users => {
        if (users.length === 0) {
          this.store.dispatch(ProfileActions.profilesLoaded({ profiles: [] }));
          this.router.navigate(['/no-access']);
        }
      }),
      filter((users): users is [User, ...User[]] => users.length > 0),
      tap(users => {
        this.store.dispatch(ProfileActions.profilesLoaded({ profiles: users }));
      }),
      switchMap(users => {
        const fases = users.map(u => u.establishmentFase);
        return fases.length > 0
          ? forkJoin(fases.map(fase => this.referenceData.getEstablishment(fase)))
          : of([]);
      }),
      tap(establishments => {
        if (establishments.length > 0) {
          this.store.dispatch(ReferenceDataActions.establishmentsLoaded({ establishments }));
        }
      }),
      catchError(error => {
        console.error('[AppFacade] Erreur lors de l\'initialisation utilisateur', error);
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.checkEstablishmentSelected());
  }

  loadEstablishmentDetail(establishmentId: string) {
    this.referenceData.getEstablishmentById(establishmentId).pipe(
      catchError(error => {
        console.error('[AppFacade] Erreur lors du chargement du détail de l\'établissement', error);
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((establishment) => {
      this.store.dispatch(
        EstablishmentActions.setEtablishment({ establishmentFase: establishment!.establishmentFase }),
      );
      localStorage.setItem('establishmentFase', establishment!.establishmentFase.toString());
      this.store.dispatch(EstablishmentActions.establishmentDetailLoaded({ establishment }));
    });
  }

  loadEstablishmentDetailByFase(establishmentFase: number) {
    this.store.select(selectAllEstablishments).pipe(
      take(1),
      filter(establishments => !!establishments && establishments.length > 0),
      map(establishments => establishments.find(establishment => establishment.establishmentFase === establishmentFase)),
      filter(establishment => !!establishment),
      tap(establishment => {
        this.store.dispatch(
          EstablishmentActions.setEtablishment({ establishmentFase: establishment!.establishmentFase }),
        );
        localStorage.setItem('establishmentFase', establishment!.establishmentFase.toString());
      }),
      switchMap(establishment => this.referenceData.getEstablishmentById(establishment!.id)),
      catchError(error => {
        console.error('[AppFacade] Erreur lors du chargement du détail de l\'établissement', error);
        return EMPTY;
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(
      (establishment) => {
        this.store.dispatch(EstablishmentActions.establishmentDetailLoaded({ establishment }));
      }
    );
  }

  private checkEstablishmentSelected() {
    this.store.select(selectAllProfiles).pipe(
      take(1),
      filter(profiles => !!profiles && profiles.length > 0),
      switchMap((profiles) => {
        const establishmentFase = Number(this.route.snapshot.queryParamMap.get('establishmentFase'));
        if (establishmentFase) {
          return of(profiles?.find(p => p.establishmentFase === establishmentFase) ?? profiles![0]);
        }

        const establishmentFaseFromLocalStorage = localStorage.getItem('establishmentFase');
        if (establishmentFaseFromLocalStorage) {
          return of(profiles?.find(p => p.establishmentFase === Number(establishmentFaseFromLocalStorage)) ?? profiles![0]);
        }

        return of(profiles![0]);
      }),
    ).subscribe(profile => {
      if (!profile) {
        this.router.navigate(['/no-access']);
        return;
      }
      this.loadEstablishmentDetailByFase(profile.establishmentFase);
    });
  }

}
